import React, { useMemo } from 'react';
import { string } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
} from '../../components';

import css from './ListingCard.module.css';
import { FavoriteTwoTone } from '@mui/icons-material';
import SectionVibeCheck from '../../containers/ListingPage/Sections/SectionVibeCheck';
import PriceSection from '../OrderPanel/PriceSection';
import {
  getListingCategory,
  getListingCategoryLabel,
} from '../../containers/EditListingPage/listingTypeUtils';
import TooltipComponent from '../Tooltip/Tooltip';
import { LISTING_CATEGORIES, USER_TYPES } from '../../containers/EditListingPage/listingTypeConfig';
import { capitalize, isNil, sample, startCase } from 'lodash';
import { parseBoolean } from '../../containers/ListingPage/listingFieldUtils';
import {
  Avatar,
  Badge,
  Box,
  HStack,
  Icon,
  StackSeparator,
  Tag,
  Text,
  VStack,
  Wrap,
} from '@chakra-ui/react';

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = price?.amount === 0 ? 'Free' : formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  // TODO: FOOTFALL - update price to say free if 0
  // return price.amount === 0 ? (
  //   <div className={css.freePrice}>{formattedPrice}</div>
  // ) : (
  //   <div className={css.price}>
  //     <div title={priceTitle}>
  //       <span>
  //         From <span className={css.priceValue}>{formattedPrice}</span>
  //       </span>
  //     </div>
  //     {isBookable ? (
  //       <div className={css.perUnit}>
  //         <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
  //       </div>
  //     ) : null}
  //   </div>
  // );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
  } = props;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  // Render the listing image only if listing images are enabled in the listing type
  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showFavouriteIcon = false,
    isSearchPage = true,
    showDropIn = true,
    infoClassName,
    isFirstCard = false,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const infoClasses = infoClassName || css.info;

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorUserType = author?.attributes?.profile?.publicData?.userType;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const formattedPrice = `£${Math.round(price.amount / 100)}`;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(
        k => k.startsWith(variantPrefix) || k.startsWith('square-small2x')
      )
    : [];

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  const listingCategory = getListingCategory(listing);

  const randomFeature = useMemo(() => {
    switch (listingCategory) {
      case LISTING_CATEGORIES.GYM:
        return sample(publicData['fitnessVenueAmenities']);
      case LISTING_CATEGORIES.SPORTS_CLUB:
        return sample(publicData['clubAmenities']);
      case LISTING_CATEGORIES.CLASS:
        return null;
      case LISTING_CATEGORIES.PERSONAL_TRAINER:
        return sample(publicData['personalTrainerAmenities']);
      default:
        return null;
    }
  }, [publicData]);

  const AuthorAvatar = () => {
    return (
      author?.id && (
        <HStack gap="3" mt={0} mb={0} justify={'flex-start'} align={'center'}>
          <Avatar.Root size={'xs'}>
            <Avatar.Image src={author?.profileImage?.attributes?.variants['square-small']?.url} />
            <Avatar.Fallback />
          </Avatar.Root>
          <Text fontWeight="medium" textStyle="sm" color={'lightGreyText'} mt={0} mb={0}>
            {author?.attributes?.profile?.displayName}
          </Text>
        </HStack>
      )
    );
  };

  const ListingSubtitle = () => {
    let subtitle = '';

    switch (listingCategory) {
      case LISTING_CATEGORIES.CLASS:
        subtitle = <AuthorAvatar />;

        break;
      case LISTING_CATEGORIES.PERSONAL_TRAINER:
        if (authorUserType === USER_TYPES.FREELANCE_PERSONAL_TRAINER) {
          const feature = capitalize(startCase(randomFeature));
          if (!isNil(feature))
            subtitle = (
              <Text
                fontWeight="medium"
                textStyle="sm"
                color={'lightGreyText'}
                mt={0}
                mb={0}
                p={0}
                textOverflow={'ellipsis'}
              >
                {feature}
              </Text>
            );
        } else {
          subtitle = <AuthorAvatar />;
        }

        break;

      default:
        if (!isNil(randomFeature)) {
          subtitle = (
            <Text
              fontWeight="medium"
              textStyle="sm"
              color={'lightGreyText'}
              mt={0}
              mb={0}
              p={0}
              textOverflow={'ellipsis'}
            >
              {capitalize(startCase(randomFeature))}
            </Text>
          );
        }
    }

    return <div className={css.subtitleText}>{subtitle}</div>;
  };

  const Title = () => (
    <Text mt={0} mb={0} p={0} fontWeight="semibold" textStyle="md" color={'almostBlack'}>
      {title}
    </Text>
  );

  const ListingTagsSection = () => (
    <Wrap mt={'0.1rem'} align={'flex-start'}>
      <SectionVibeCheck publicData={publicData} />
      {parseBoolean(publicData?.dropInAvailable) && showDropIn && (
        <TooltipComponent
          content={'No membership required through a venue to join'}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Tag.Root
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
            size={'sm'}
            variant={'solid'}
            rounded={'xl'}
          >
            <Tag.Label>Drop-in</Tag.Label>
          </Tag.Root>
        </TooltipComponent>
      )}
    </Wrap>
  );

  const SearchPageListingCardInfo = () => (
    <VStack align="flex-start" gap={'2'} separator={<StackSeparator />} overflow={'hidden'}>
      <VStack align={'flex-start'} gap={'1'}>
        <Title />
        <ListingSubtitle />
        <ListingTagsSection />
      </VStack>
      <Text color={'lightGreyText'} textStyle="sm" mt={0} mb={0} p={0}>
        <PriceSection
          isListingCard
          currentListing={currentListing}
          formattedPrice={formattedPrice}
        />
      </Text>
    </VStack>
  );

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <AspectRatioWrapper
        className={classNames(css.aspectRatioWrapper, {
          [css.aspectRatioWrapperMobile]: infoClassName,
        })}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={classNames(css.rootForImage, {
            [css.aspectRatioWrapperMobile]: infoClassName,
          })}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
        {isSearchPage && (
          <Badge
            p={'0.1rem 0.2rem'}
            bgColor={'white'}
            variant="surface"
            align={'flex-start'}
            position={'absolute'}
            top={'16px'}
            left={'16px'}
            color={'almostBlack'}
            rounded={'xl'}
          >
            {listingCategory?.icon}
            {getListingCategoryLabel(listingCategory?.id, publicData)}
          </Badge>
        )}
        {showFavouriteIcon && (
          <Icon
            className={css.favouritesIcon}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              alert(`Favourites not implemented yet!`);
            }}
          >
            <FavoriteTwoTone fontSize={'large'} />
          </Icon>
        )}
      </AspectRatioWrapper>
      <Box
        mt={{ mdDown: 0, base: '0.5rem' }}
        p={{ mdDown: isSearchPage ? '1rem' : '0' }}
        maxWidth={'100%'}
      >
        {isSearchPage ? (
          <SearchPageListingCardInfo />
        ) : (
          <VStack align="flex-start" gap={0}>
            <Title />
            <Text color={'lightGreyText'} textStyle="sm" mt={0} mb={0} p={0}>
              {getListingCategoryLabel(listingCategory?.id, publicData)}
            </Text>
            <ListingTagsSection />
          </VStack>
        )}
      </Box>
    </NamedLink>
  );
};

export default ListingCard;
